import { isNativeAppWebview } from 'core/utils/webapp';
import React, { KeyboardEvent, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectCustomizableOption } from './SelectCustomizable';

let globalSelectCounter = 0;

type ActionMeta = { action: string; removedValue?: SelectCustomizableOption };

type StyleFn = (base: Record<string, unknown>, state?: Record<string, unknown>) => Record<string, unknown>;

type SharedStyles = {
  menu: StyleFn;
  menuPortal: StyleFn;
  control: StyleFn;
  option: StyleFn;
};

type SelectStyles = SharedStyles & {
  valueContainer?: StyleFn;
};

type SharedProps = {
  _listboxId: string;
  _focusedOptionId: string | null;
  _onOptionFocusChange: (id: string) => void;
  _errorId: string | undefined;
  _hasError: boolean;
  _required: boolean;
  _removeLabel: string;
  _valueId: string;
  onKeyDown: (event: KeyboardEvent) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  tabSelectsValue: boolean;
  iconTypeIndicator: string | undefined;
  showValuesAsList: boolean;
  showValuesAsText: boolean;
  useShortLabels: boolean | undefined;
};

type UseSelectCustomizableParams = {
  inputId?: string;
  name?: string;
  isSearchable?: boolean;
  error?: string;
  noOptionsMessage?: string;
  label?: ReactNode;
  ariaLabel?: string;
  placeholder?: ReactNode;
  required?: boolean;
  variant?: 'default' | 'subtle';
  size?: 'default' | 'small';
  tagSize?: 'sm' | 'md';
  iconTypeIndicator?: string;
  multiValueDisplay?: 'inline' | 'sortedList';
  showValuesAsText?: boolean;
  useShortLabels?: boolean;
  onChange?: (value: unknown, actionMeta?: ActionMeta) => void;
  onCreateOption?: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

type UseSelectCustomizableReturn = {
  resolvedInputId: string;
  labelId: string;
  listboxId: string;
  errorId: string;
  selectRef: MutableRefObject<unknown>;
  finalIsSearchable: boolean | undefined;
  finalMenuPlacement: 'top' | 'auto';
  getNoOptionsMessage: () => string;
  getCreateLabel: (text: string) => string;
  getScreenReaderStatus: (state: { count: number }) => string;
  sharedStyles: SharedStyles;
  selectStyles: SelectStyles;
  resolvedPlaceholder: ReactNode;
  resolvedAriaLabel: string | undefined;
  labelTitle: string;
  className: string;
  isSortedList: boolean;
  sharedProps: SharedProps;
  handleCreatableChange: (newValue: unknown, actionMeta?: ActionMeta) => void;
  formatGroupLabel: (data: { label: string; options: unknown[] }) => React.ReactElement;
  getOptionLabel: (data: SelectCustomizableOption, meta: { context: string }) => ReactNode;
};

type SelectInstance = {
  openMenu?: (position: string) => void;
  // Nodo del input que react-select haya renderizado (el propio o su DummyInput).
  inputRef?: HTMLInputElement | null;
  // Props que el StateManager pasa al Select interno ya RESUELTAS (prop controlada
  // del consumidor o estado interno, lo que aplique).
  props?: { menuIsOpen?: boolean };
};

type SelectRefValue = {
  select?: SelectInstance & { select?: SelectInstance };
};

function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPad = /ipad/.test(userAgent) || (navigator.maxTouchPoints > 1 && /macintosh/.test(userAgent));
  const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  return isIPad || isMobile || isNativeAppWebview();
}

function getControlBorderStyles(hasError: boolean): Record<string, unknown> {
  if (hasError) {
    return {
      border: '2px solid var(--select-customizable-border-error)',
      '&:hover': { borderColor: 'var(--select-customizable-border-error)' },
    };
  }
  return {
    border: '1px solid var(--select-customizable-border-default)',
    '&:hover': { border: '2px solid var(--select-customizable-border-hover)' },
  };
}

function getInnerSelectInstance(ref: MutableRefObject<unknown>): SelectInstance | null {
  if (!ref.current) return null;
  // ref.current = StateManager instance
  // ref.current.select = inner Select (regular) OR Creatable (creatable)
  // ref.current.select.select = inner Select (creatable only)
  const { select: inner } = ref.current as SelectRefValue;
  if (!inner) return null;
  return inner.select ?? inner;
}

function keepLatestMenuIsOpen(_previous: boolean, next: boolean): boolean {
  return next;
}

// Aviso en desarrollo: el select cae al placeholder como nombre accesible
// cuando no hay `label` ni `aria-label`. Es un stopgap; lo suyo es un nombre
// específico y con sentido para el campo (ver "Deuda técnica" en la mdx).
function warnMissingAccessibleName(): void {
  if (process.env.NODE_ENV === 'production') return;
  console.warn(
    '[SelectCustomizable] Sin `label` ni `aria-label`: el nombre accesible cae al `placeholder`. ' +
      'Pasa un `label` visible o un `aria-label` específico y con sentido para el campo.'
  );
}

// Sin separador se reproduciría el textContent pegado del DOM ("Clase CarmenNaves espaciales").
const TEXT_SEGMENT_SEPARATOR = ', ';

// Quien llama necesita distinguir un NOMBRE (`ariaLabel`, label, JSX) de un IDENTIFICADOR
// (`value`): poner un guid en un `aria-label` sustituye un nombre correcto por uno ilegible.
export type OptionTextSource = 'ariaLabel' | 'label' | 'jsx' | 'value' | 'none';

export type ResolvedOptionText = {
  text: string;
  source: OptionTextSource;
};

const NO_OPTION_TEXT: ResolvedOptionText = { text: '', source: 'none' };

// La clave lleva delante la longitud del value para que ("a|b", "") y ("a", "|b") no colisionen.
const warnedJsxLabelKeys = new Set<string>();
const warnedLabelNodes = new WeakSet<object>();

// Absorbe las resoluciones repetidas dentro de un mismo render (`AccessibleOption` resuelve
// cada opción visible en todos). No acierta ENTRE renders: los consumidores construyen
// `label: <X/>` dentro de un `.map()`, así que cada render crea nodos nuevos.
const jsxTextCache = new WeakMap<object, string>();

function getWarnKey(value: unknown, text: string): string {
  const valueKey = getPrimitiveText(value);
  return `${valueKey.length}:${valueKey}${text}`;
}

// Aviso en desarrollo: deducir el texto del JSX es una heurística —solo ve lo que viaja por
// `children`, no lo que pinte un componente hijo—, así que lo suyo es que lo declare el consumidor.
function warnJsxOptionLabel(labelNode: ReactNode, value: unknown, resolvedText: string): void {
  if (process.env.NODE_ENV === 'production') return;

  const node = labelNode as unknown as object;
  if (warnedLabelNodes.has(node)) return;
  warnedLabelNodes.add(node);

  const key = getWarnKey(value, resolvedText);
  if (warnedJsxLabelKeys.has(key)) return;
  warnedJsxLabelKeys.add(key);
  console.warn(
    `[SelectCustomizable] La opción "${getPrimitiveText(value)}" tiene un \`label\` JSX sin \`ariaLabel\`: el texto que ` +
      `anuncia el lector se deriva del propio JSX ("${resolvedText}"). Pasa un \`ariaLabel\` con el texto de la ` +
      'opción, o un `label` string y el JSX por `formatOptionLabel`.'
  );
}

// split/join y no `replaceAll`: los targets incluyen iOS Safari < 13.4 y el build no inyecta polyfills.
function normalizeAccessibleText(text: string): string {
  return text.split(/\s+/).join(' ').split(' ,').join(',').trim();
}

function flattenChildren(node: ReactNode, flat: ReactNode[]): void {
  if (Array.isArray(node)) {
    node.forEach((child) => flattenChildren(child, flat));
    return;
  }
  flat.push(node);
}

// Sin duplicar la pausa: <div>Madrid,</div><div>España</div> se lee "Madrid, España".
function joinTextSegments(segments: string[]): string {
  return segments.reduce((accumulated, segment) => {
    if (!accumulated) return segment;
    const separator = /[,;.:]\s*$/.test(accumulated) ? ' ' : TEXT_SEGMENT_SEPARATOR;
    return accumulated + separator + segment;
  }, '');
}

// Los nodos de texto contiguos se concatenan tal cual ("3.º Primaria", no "3.º, Primaria");
// lo que aporta cada hijo-elemento se une con `, ` (tres <div> → "Clase Carmen, 3.º Primaria").
function extractNodeText(node: ReactNode): string {
  const children: ReactNode[] = [];
  flattenChildren(node, children);

  const segments: string[] = [];
  let textRun = '';

  function flushTextRun(): void {
    if (textRun.trim()) segments.push(textRun);
    textRun = '';
  }

  children.forEach((child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      textRun += String(child);
      return;
    }
    // Un componente que genere su texto por dentro es opaco: solo se lee su `children`.
    if (!React.isValidElement(child)) return;
    flushTextRun();
    const { children: nested } = child.props as { children?: ReactNode };
    const nestedText = extractNodeText(nested);
    if (nestedText) segments.push(nestedText);
  });
  flushTextRun();

  return joinTextSegments(segments);
}

function extractJsxText(label: ReactNode): string {
  const cacheKey = label as unknown as object;
  const cached = jsxTextCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const text = normalizeAccessibleText(extractNodeText(label));
  jsxTextCache.set(cacheKey, text);
  return text;
}

function resolveLabelText(label: ReactNode): ResolvedOptionText {
  if (typeof label === 'string') return { text: label, source: 'label' };
  if (typeof label === 'number') return { text: String(label), source: 'label' };
  if (React.isValidElement(label) || Array.isArray(label)) return { text: extractJsxText(label), source: 'jsx' };
  return NO_OPTION_TEXT;
}

/**
 * Texto de un `value` primitivo, o cadena vacía. Un `value` objeto se descarta a propósito:
 * stringificarlo daría el "[object Object]" que hay que evitar. Exportada para que el
 * `filterOption` del componente aplique la misma regla en vez de reimplementarla.
 */
export function getPrimitiveText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '';
}

// `value` es un identificador, no un nombre: último recurso solo para una opción sin `label`.
function resolveFallbackText(value: unknown): ResolvedOptionText {
  const text = getPrimitiveText(value);
  return text ? { text, source: 'value' } : NO_OPTION_TEXT;
}

/**
 * Única implementación de la precedencia `ariaLabel` → `label` → texto del JSX → `value`.
 * Quien necesite otra política no la reimplementa: mira el `source`.
 *
 * Un JSX opaco NO cae al `value`: el label sí pinta un nombre, y anunciar un guid en su lugar
 * es peor que no anunciar nada (DialogAddUnit).
 *
 * Independiente del contexto (`value` / `menu`) porque react-select la invoca siempre con un
 * único argumento: aquí no se aplica `useShortLabels` aunque el control pinte el `shortLabel`.
 */
export function resolveOptionText(data: SelectCustomizableOption | null | undefined): ResolvedOptionText {
  if (!data) return NO_OPTION_TEXT;

  const { ariaLabel, label, value } = data;
  if (typeof ariaLabel === 'string' && ariaLabel.trim()) return { text: ariaLabel, source: 'ariaLabel' };

  const fromLabel = resolveLabelText(label);
  if (fromLabel.source === 'jsx') {
    warnJsxOptionLabel(label, value, fromLabel.text);
    return fromLabel.text ? fromLabel : NO_OPTION_TEXT;
  }

  return fromLabel.text ? fromLabel : resolveFallbackText(value);
}

/**
 * Canal de datos que consume react-select como `getOptionLabel`: región `aria-live`, valor
 * enfocado y filtrado. Lo que se pinta lo decide `formatOptionLabel`.
 *
 * Un `ariaLabel` se compone con la etiqueta visible igual que en el `role="option"`, para que
 * los dos canales anuncien lo mismo.
 */
export function getOptionAccessibleText(data: SelectCustomizableOption | null | undefined): string {
  const { text, source } = resolveOptionText(data);
  return data && source === 'ariaLabel' ? composeAccessibleName(data, text) : text;
}

// Texto VISIBLE de una opción: nunca mira `ariaLabel` (alternativa oculta) ni `value`.
function resolveDisplayText(data: SelectCustomizableOption | null | undefined): ResolvedOptionText {
  if (!data) return NO_OPTION_TEXT;
  return resolveLabelText(data.label);
}

/** Atajo de `resolveDisplayText` para ordenar, resumir o rellenar un `title`. */
export function getOptionDisplayText(data: SelectCustomizableOption | null | undefined): string {
  return resolveDisplayText(data).text;
}

// Lista explícita en vez de `\p{P}`: los targets incluyen iOS Safari 11.0, sin property escapes.
const WORD_SEPARATORS = /[\s.,;:()[\]{}"'«»¿?¡!·/|\-–—]+/;

function toWords(text: string): string[] {
  return text.toLowerCase().split(WORD_SEPARATORS).filter(Boolean);
}

// Por palabras completas y contiguas: "Mat" no está contenido en "Matemáticas".
function containsWords(text: string, target: string): boolean {
  const words = toWords(text);
  const targetWords = toWords(target);
  if (!targetWords.length) return false;

  return words.some((_, start) => targetWords.every((word, offset) => words[start + offset] === word));
}

/**
 * WCAG 2.1 · 2.5.3 «Label in Name»: si el `label` es un texto visible, el nombre accesible
 * tiene que contenerlo, o quien usa control por voz dice lo que ve y no activa nada. Por eso
 * se compone —visible primero— en vez de reemplazar: "Matemáticas, Asignatura troncal".
 * Con `label` JSX no se compone: no hay etiqueta de texto que contener.
 */
export function composeAccessibleName(data: SelectCustomizableOption, accessibleText: string): string {
  const visible = resolveDisplayText(data);
  if (visible.source !== 'label' || !visible.text.trim()) return accessibleText;
  if (containsWords(accessibleText, visible.text)) return accessibleText;

  return `${visible.text}${TEXT_SEGMENT_SEPARATOR}${accessibleText}`;
}

function setOrRemoveAttribute(element: HTMLElement, name: string, value: string | null): void {
  if (value) {
    element.setAttribute(name, value);
    return;
  }
  element.removeAttribute(name);
}

function getLabelTitle(labelValue: ReactNode): string {
  if (typeof labelValue === 'string') return labelValue;
  if (!React.isValidElement(labelValue)) return '';

  const { children } = labelValue.props as { children?: ReactNode };
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.filter((child) => typeof child === 'string').join(' ');

  return '';
}

export default function useSelectCustomizable({
  inputId,
  name,
  isSearchable,
  error,
  noOptionsMessage,
  label,
  ariaLabel,
  placeholder,
  required,
  variant = 'default',
  size = 'default',
  tagSize = 'md',
  iconTypeIndicator,
  multiValueDisplay = 'inline',
  showValuesAsText = false,
  useShortLabels,
  onChange,
  onCreateOption,
  onKeyDown: consumerOnKeyDown,
  onMenuOpen: consumerOnMenuOpen,
  onMenuClose: consumerOnMenuClose,
}: UseSelectCustomizableParams): UseSelectCustomizableReturn {
  const { t } = useTranslation(['placeholder']);

  const instanceIdRef = useRef(`select-accessible-${++globalSelectCounter}`);
  const resolvedInputId = inputId || name || instanceIdRef.current;
  const labelId = `${resolvedInputId}-label`;
  const listboxId = `${resolvedInputId}-listbox`;
  const errorId = `${resolvedInputId}-error`;
  const valueId = `${resolvedInputId}-value`;

  const selectRef = useRef<unknown>(null);
  // El ref es lo que lee `getScreenReaderStatus`, que tiene que ser estable (TAN-10401);
  // el estado paralelo no se lee nunca: existe solo para provocar un render al abrir/cerrar
  // (la fuente de verdad del efecto es `inner.props.menuIsOpen`, ya resuelta). `useReducer` y no
  // `useState` sin desestructurar (Sonar S6754). Guarda el último booleano: en React 17 repetir la
  // apertura no re-renderiza; en 18 sí re-renderiza, pero se descarta sin commit.
  const menuIsOpenRef = useRef(false);
  const [, rerenderOnMenuToggle] = useReducer(keepLatestMenuIsOpen, false);
  const [focusedOptionId, setFocusedOptionId] = useState<string | null>(null);

  const isOnMobileDevice = useMemo(isMobileDevice, []);
  const finalIsSearchable = isOnMobileDevice ? false : isSearchable;
  const finalMenuPlacement: 'top' | 'auto' = isOnMobileDevice ? 'top' : 'auto';

  const isSortedList = multiValueDisplay === 'sortedList';

  // Nombre accesible: si hay label visible manda el aria-labelledby; si no,
  // usa el aria-label del consumidor y, en su defecto, cae al placeholder
  // (que siempre es un string) para que el combobox nunca quede sin nombre.
  // `||` (no `??`) para que un placeholder="" también caiga al default.
  const resolvedPlaceholder = placeholder || t('Select');
  const placeholderName = typeof resolvedPlaceholder === 'string' ? resolvedPlaceholder : undefined;
  const resolvedAriaLabel = ariaLabel ?? (label ? undefined : placeholderName);
  const labelTitle = getLabelTitle(label);

  const hasAccessibleName = Boolean(label) || Boolean(ariaLabel);
  useEffect(() => {
    if (!hasAccessibleName) warnMissingAccessibleName();
  }, [hasAccessibleName]);

  // TAN-10504 — patrón combobox cuando react-select no usa nuestro `Input`.
  // Con `isSearchable={false}`, `renderInput()` ignora `components.Input` y monta su
  // `DummyInput` interno (un <input readOnly> invisible) con solo aria-autocomplete,
  // aria-label y aria-labelledby: `AccessibleInput` queda inerte y el lector anuncia un
  // campo de texto en vez de un combo contraído/expandido (WCAG 2.1 · 4.1.2). react-select
  // v3 no expone `components.DummyInput`, así que el patrón se aplica sobre el nodo DOM.
  // OJO: siete de los ocho atributos no existen en las props React de ese input, pero
  // `aria-autocomplete` SÍ (react-select se lo pasa como prop al DummyInput): el override
  // solo se sostiene porque este efecto se re-aplica tras cada render (ver el comentario
  // final sobre las dependencias). `rerenderOnMenuToggle` existe solo para provocar el
  // render (y por tanto esta pasada) al abrir/cerrar el menú, que es además lo que hace
  // que el id del valor se recoja justo tras la primera selección.
  useEffect(() => {
    if (finalIsSearchable !== false) return;

    const inner = getInnerSelectInstance(selectRef);
    const input = inner?.inputRef;
    // El modo creatable no reenvía `isSearchable` a react-select, así que ahí sigue
    // renderizándose un `Input` real gobernado por React: solo se decora el DummyInput.
    if (!input?.readOnly) return;

    // `inner.props.menuIsOpen` es el estado ya resuelto (prop controlada o interno). No
    // sirve el booleano de `rerenderOnMenuToggle`, derivado de los callbacks: con
    // `menuIsOpen={false}` controlado, react-select dispara onMenuOpen igualmente
    // (openMenu → onMenuOpen es incondicional) sin abrir nada, y el atributo se quedaría en
    // "true" para toda la sesión de foco.
    const menuIsOpen = Boolean(inner?.props?.menuIsOpen);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', menuIsOpen ? 'true' : 'false');
    input.setAttribute('aria-controls', listboxId);
    // Este modo nunca filtra, así que el "list" que pone react-select prometería un
    // autocompletado que no existe.
    input.setAttribute('aria-autocomplete', 'none');

    setOrRemoveAttribute(input, 'aria-activedescendant', focusedOptionId);
    setOrRemoveAttribute(input, 'aria-invalid', error ? 'true' : null);
    setOrRemoveAttribute(input, 'aria-required', required ? 'true' : null);

    // El valor seleccionado se pinta en un <div> hermano que el lector se salta al enfocar,
    // así que se referencia por aria-describedby, pero solo si ese nodo ya está en el DOM:
    // un id colgante no lo anuncia nadie. Y axe tampoco lo caza — aria-valid-attr-value lo
    // deja en "incomplete", nunca en violation —, así que los tests lo asertan directamente.
    // El modo buscable resuelve lo mismo declarativamente en getDescribedBy
    // (SelectCustomizable.tsx): si cambias un lado, revisa el otro.
    const hasRenderedValue = Boolean(document.getElementById(valueId));
    const describedBy = [hasRenderedValue ? valueId : null, error ? errorId : null].filter(Boolean).join(' ');
    setOrRemoveAttribute(input, 'aria-describedby', describedBy || null);
    // Sin lista de dependencias A PROPÓSITO, y es obligatorio que siga así: (1) la presencia
    // del valor se lee del DOM, no de una dependencia, y hay que resincronizar en cada render
    // para que un valor que desaparece no deje un aria-describedby colgante; y (2) React sí
    // gestiona aria-autocomplete en el DummyInput, así que cualquier remontaje lo devuelve a
    // "list" y esta pasada tiene que correr tras cada render para re-corregirlo. Añadir un
    // array de deps "para optimizar" rompería ambas cosas en silencio (exhaustive-deps no se
    // activa cuando el array falta). Es idempotente y solo escribe atributos.
  });

  const className = `select-customizable select-customizable--${variant} select-customizable--size-${size} select-customizable--tag-${tagSize}${
    error ? ' select-customizable--error' : ''
  }`;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Con el menú abierto, Escape solo cierra el listbox (patrón combobox). react-select no
    // frena su propagación, y un `Dialog` que contenga el select (el `Modal` de MUI ignora
    // `defaultPrevented`) se cerraría con el mismo Escape. Sin `preventDefault`: react-select
    // lo interpreta como "no gestionar la tecla" y dejaría el menú abierto. Se lee
    // `inner.props.menuIsOpen` y no `menuIsOpenRef` por lo mismo que en el efecto de
    // decoración: con `menuIsOpen={false}` controlado el ref se queda en true y el
    // contenedor dejaría de recibir el Escape.
    if (event.key === 'Escape' && getInnerSelectInstance(selectRef)?.props?.menuIsOpen) {
      event.stopPropagation();
    }

    if (event.key === 'Enter' && !menuIsOpenRef.current) {
      event.preventDefault();
      const instance = getInnerSelectInstance(selectRef);
      if (instance && typeof instance.openMenu === 'function') {
        instance.openMenu('first');
      }
    }
  }, []);

  // El render de `rerenderOnMenuToggle` solo lo aprovecha el efecto de decoración del modo
  // no buscable (que sale por `return` si el select es buscable): en el resto de selects,
  // provocarlo solo costaría un re-render del componente por cada apertura/cierre.
  const handleMenuOpen = useCallback(() => {
    menuIsOpenRef.current = true;
    if (finalIsSearchable === false) rerenderOnMenuToggle(true);
  }, [finalIsSearchable]);

  const handleMenuClose = useCallback(() => {
    menuIsOpenRef.current = false;
    if (finalIsSearchable === false) rerenderOnMenuToggle(false);
    setFocusedOptionId(null);
  }, [finalIsSearchable]);

  const onOptionFocusChange = useCallback((id: string) => {
    setFocusedOptionId(id || null);
  }, []);

  const composedKeyDown = useCallback(
    (event: KeyboardEvent) => {
      handleKeyDown(event);
      if (consumerOnKeyDown) consumerOnKeyDown(event);
    },
    [handleKeyDown, consumerOnKeyDown]
  );

  const composedMenuOpen = useCallback(() => {
    handleMenuOpen();
    if (consumerOnMenuOpen) consumerOnMenuOpen();
  }, [handleMenuOpen, consumerOnMenuOpen]);

  const composedMenuClose = useCallback(() => {
    handleMenuClose();
    if (consumerOnMenuClose) consumerOnMenuClose();
  }, [handleMenuClose, consumerOnMenuClose]);

  // react-select v3 Creatable tiene un bug de igualdad por referencia: cuando el
  // menú se re-renderiza entre el hover y el click (frecuente al escribir), la
  // referencia de la opción clicada deja de coincidir con state.newOption, y la
  // opción nueva se cuela por onChange en vez de disparar onCreateOption. Se
  // reenvía cualquier valor __isNew__ a onCreateOption.
  const handleCreatableChange = useCallback(
    (newValue: unknown, actionMeta?: ActionMeta) => {
      const candidate = Array.isArray(newValue) ? newValue.at(-1) : (newValue as SelectCustomizableOption | null);
      if (candidate?.__isNew__ && onCreateOption) {
        onCreateOption(String(candidate.value));
        return;
      }
      if (onChange) onChange(newValue, actionMeta);
    },
    [onChange, onCreateOption]
  );

  const getNoOptionsMessage = useCallback(() => {
    return noOptionsMessage ?? t('placeholder:No options');
  }, [noOptionsMessage, t]);

  // Solo se anuncia el recuento con el menú abierto; cerrado, react-select daría
  // un "0 results available" falso. Texto en inglés (i18n fuera de alcance).
  const getScreenReaderStatus = useCallback(({ count }: { count: number }): string => {
    if (menuIsOpenRef.current) return `${count} result${count === 1 ? '' : 's'} available`;
    return '';
  }, []);

  const getCreateLabel = useCallback((text: string) => `${t('placeholder:Create new section')}: ${text}`, [t]);

  const formatGroupLabel = useCallback(
    (data: { label: string; options: unknown[] }): React.ReactElement => (
      <div className="select-customizable__group-label">
        <span>{data.label}</span>
        <span className="select-customizable__group-badge">{data.options.length}</span>
      </div>
    ),
    []
  );

  // Canal de PRESENTACIÓN: se cablea como `formatOptionLabel`, así que puede devolver JSX
  // y sí conoce el contexto (`value` / `menu`). Lo que anuncia el lector NO sale de aquí,
  // sino de `getOptionAccessibleText`: si tocas uno, revisa el otro.
  const getOptionLabel = useCallback(
    (data: SelectCustomizableOption, { context }: { context: string }): ReactNode =>
      useShortLabels && context === 'value' && data.shortLabel ? data.shortLabel : data.label,
    [useShortLabels]
  );

  const sharedStyles = useMemo<SharedStyles>(
    () => ({
      menu: (provided) => ({ ...provided, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      control: (provided) => ({
        ...provided,
        boxShadow: undefined,
        cursor: 'pointer',
        borderRadius: 'var(--select-customizable-border-radius)',
        transition: 'border-color 0.4s',
        ...getControlBorderStyles(!!error),
      }),
      option: (provided) => ({
        ...provided,
        backgroundColor: 'transparent',
        color: 'inherit',
      }),
    }),
    [error]
  );

  const selectStyles = useMemo<SelectStyles>(() => {
    if (!isSortedList) return sharedStyles;
    return {
      ...sharedStyles,
      control: (base, state = {}) => {
        const shared = sharedStyles.control(base, state);
        const { hasValue, isFocused } = state as { hasValue?: boolean; isFocused?: boolean };
        if (hasValue && !isFocused) {
          return {
            ...shared,
            border: '1px solid var(--select-customizable-border-default)',
            boxShadow: 'none',
          };
        }
        return shared;
      },
      valueContainer: (base) => ({
        ...base,
        padding: '2px 8px',
        overflow: 'hidden',
        cursor: 'pointer',
      }),
    };
  }, [isSortedList, sharedStyles]);

  const sharedProps = useMemo<SharedProps>(
    () => ({
      _listboxId: listboxId,
      _focusedOptionId: focusedOptionId,
      _onOptionFocusChange: onOptionFocusChange,
      _errorId: error ? errorId : undefined,
      _hasError: !!error,
      _required: !!required,
      _removeLabel: t('placeholder:Remove'),
      _valueId: valueId,
      onKeyDown: composedKeyDown,
      onMenuOpen: composedMenuOpen,
      onMenuClose: composedMenuClose,
      tabSelectsValue: false,
      // Props que consumen los componentes custom vía selectProps
      iconTypeIndicator,
      showValuesAsList: isSortedList,
      showValuesAsText,
      useShortLabels,
    }),
    [
      listboxId,
      focusedOptionId,
      onOptionFocusChange,
      error,
      errorId,
      required,
      t,
      valueId,
      composedKeyDown,
      composedMenuOpen,
      composedMenuClose,
      iconTypeIndicator,
      isSortedList,
      showValuesAsText,
      useShortLabels,
    ]
  );

  return {
    resolvedInputId,
    labelId,
    listboxId,
    errorId,
    selectRef,
    finalIsSearchable,
    finalMenuPlacement,
    getNoOptionsMessage,
    getCreateLabel,
    getScreenReaderStatus,
    sharedStyles,
    selectStyles,
    resolvedPlaceholder,
    resolvedAriaLabel,
    labelTitle,
    className,
    isSortedList,
    sharedProps,
    handleCreatableChange,
    formatGroupLabel,
    getOptionLabel,
  };
}
